import { hasValue } from '@util/type';
import * as R from 'ramda';

type CacheKeyFn<T> = (...args: T[]) => any;
type CacheParentKeyFn<T> = (...args: T[]) => any;
type MemoizeFn<T> = (...args: T[]) => any;
type MemoizeWith = <T> (cacheKeyFn: CacheKeyFn<T>, cacheParentKeyFn: CacheParentKeyFn<T>, memoizeFn: MemoizeFn<T>) => (...args: T[]) => any;
/**
 * Creates a new function that, when invoked,
 * caches the result of the execution of memoizeFn with key as result of the execution of cacheKeyFn, and returns that result.
 * Subsequent calls to the memoized fn that produce the same output of executing cacheKeyFn will not result in an additional call to memoizeFn,
 * instead, the cached result will be returned.
 *
 * @param cacheKeyFn function used to find the key in the cache
 * @param cacheParentKeyFn function used to find the parent key in the cache
 * @param memoizeFn function to memoize
 */
export const memoizeWith: MemoizeWith = <T>(cacheKeyFn: CacheKeyFn<T>, cacheParentKeyFn: CacheParentKeyFn<T>, memoizeFn: MemoizeFn<T>) => {
  let cache = {};
  return (...args: T[]) => {
    const key = cacheKeyFn(...args);
    if (cache[key]) {
      return cache[key];
    }
    // reset the state
    const parentKey = cacheParentKeyFn(...args);
    cache = hasValue(cache[parentKey]) ? R.pick([parentKey], cache) : {};
    cache[key] = memoizeFn(...args);
    return cache[key];
  };
};
